import {
  Add as AddIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography,
  Skeleton,
} from '@mui/material';
import React, { useCallback, useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { useImageBlob } from '../../hooks/useImageBlob';
import { fileToBlobUrl, revokeBlobUrls } from '../../utils/imageUtils';

const ProductForm = ({ product, onSubmit, onCancel }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: product?.name || '',
    category: product?.category || '',
    specifications: product?.specifications || [{ name: '', value: '' }],
  });
  const [mainImage, setMainImage] = useState(null);
  const [existingGalleryImages, setExistingGalleryImages] = useState(
    product?.galleryImages || []
  );
  const [newGalleryImages, setNewGalleryImages] = useState([]);
  const [errors, setErrors] = useState({});
  const [mainImageBlobUrl, setMainImageBlobUrl] = useState(null);

  const apiUrl = process.env.REACT_APP_API_URL;

  // Use the custom hook to convert existing main image path to blob URL
  const { blobUrl: existingMainImageBlob, loading: mainImageLoading } =
    useImageBlob(product?.mainImage, apiUrl);

  const onDropMain = useCallback(
    (acceptedFiles) => {
      // Clean up previous blob URL if it exists
      if (mainImageBlobUrl) {
        revokeBlobUrls(mainImageBlobUrl);
      }

      const file = acceptedFiles[0];
      setMainImage(file);
      setMainImageBlobUrl(fileToBlobUrl(file));
    },
    [mainImageBlobUrl]
  );

  const onDropGallery = useCallback((acceptedFiles) => {
    setNewGalleryImages((prev) => [...prev, ...acceptedFiles]);
  }, []);

  const { getRootProps: getMainRootProps, getInputProps: getMainInputProps } =
    useDropzone({
      onDrop: onDropMain,
      accept: {
        'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'],
      },
      maxFiles: 1,
    });

  const {
    getRootProps: getGalleryRootProps,
    getInputProps: getGalleryInputProps,
  } = useDropzone({
    onDrop: onDropGallery,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'],
    },
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSpecificationChange = (index, field, value) => {
    setFormData((prev) => {
      const newSpecs = [...prev.specifications];
      newSpecs[index] = { ...newSpecs[index], [field]: value };
      return { ...prev, specifications: newSpecs };
    });
  };

  const addSpecification = () => {
    setFormData((prev) => ({
      ...prev,
      specifications: [...prev.specifications, { name: '', value: '' }],
    }));
  };

  const removeSpecification = (index) => {
    setFormData((prev) => ({
      ...prev,
      specifications: prev.specifications.filter((_, i) => i !== index),
    }));
  };

  const handleRemoveExistingGalleryImage = (index) => {
    setExistingGalleryImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemoveNewGalleryImage = (index) => {
    setNewGalleryImages((prev) => prev.filter((_, i) => i !== index));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = t('Product name is required');
    if (!formData.category) newErrors.category = t('Category is required');
    if (!mainImage && !product)
      newErrors.mainImage = t('Main image is required');

    const specErrors = formData.specifications.map((spec) => ({
      name: !spec.name ? t('Specification name is required') : '',
      value: !spec.value ? t('Specification value is required') : '',
    }));

    if (specErrors.some((err) => err.name || err.value)) {
      newErrors.specifications = specErrors;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const submitData = new FormData();
    submitData.append('name', formData.name);
    submitData.append('category', formData.category);
    submitData.append(
      'specifications',
      JSON.stringify(formData.specifications)
    );

    if (mainImage) {
      submitData.append('mainImage', mainImage);
    }

    submitData.append(
      'existingGalleryImages',
      JSON.stringify(existingGalleryImages)
    );
    newGalleryImages.forEach((image) => {
      submitData.append('galleryImages', image);
    });

    onSubmit(submitData);
  };

  // Cleanup blob URLs when component unmounts
  useEffect(() => {
    return () => {
      if (mainImageBlobUrl) {
        revokeBlobUrls(mainImageBlobUrl);
      }
    };
  }, [mainImageBlobUrl]);

  // Component to display a gallery image as a blob using useImageBlob
  const GalleryImageBlob = ({ imgPath, apiUrl, alt, sx }) => {
    const { blobUrl, loading } = useImageBlob(imgPath, apiUrl);
    if (loading) {
      return <Skeleton variant='rectangular' width='100%' height='100%' />;
    }
    return <Box component='img' src={blobUrl || ''} alt={alt} sx={sx} />;
  };

  return (
    <Box component='form' onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            label={t('Product Name')}
            name='name'
            value={formData.name}
            onChange={handleChange}
            error={!!errors.name}
            helperText={errors.name}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            label={t('Category')}
            name='category'
            value={formData.category}
            onChange={handleChange}
            error={!!errors.category}
            helperText={errors.category}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Typography variant='h6' gutterBottom>
            {t('Main Image')}
          </Typography>
          <Paper
            {...getMainRootProps()}
            sx={{
              p: 2,
              border: '2px dashed',
              borderColor: 'primary.main',
              textAlign: 'center',
              cursor: 'pointer',
              bgcolor: 'background.default',
            }}
          >
            <input {...getMainInputProps()} />
            <CloudUploadIcon
              sx={{ fontSize: 40, color: 'primary.main', mb: 1 }}
            />
            <Typography>
              {mainImage
                ? mainImage?.name
                : product?.mainImage
                ? t('Current Image')
                : t('Drop main image here or click to select')}
            </Typography>
            {product?.mainImage && !mainImage && (
              <Box sx={{ position: 'relative' }}>
                {mainImageLoading ? (
                  <Skeleton
                    variant='rectangular'
                    width='100%'
                    height={200}
                    sx={{ mt: 2 }}
                  />
                ) : (
                  <Box
                    component='img'
                    src={existingMainImageBlob || ''}
                    alt='Current'
                    sx={{
                      maxWidth: '100%',
                      maxHeight: 200,
                      mt: 2,
                      objectFit: 'cover',
                      borderRadius: 1,
                    }}
                  />
                )}
              </Box>
            )}
            {mainImage && (
              <Box
                component='img'
                src={mainImageBlobUrl || ''}
                alt='New'
                sx={{
                  maxWidth: '100%',
                  maxHeight: 200,
                  mt: 2,
                  objectFit: 'cover',
                  borderRadius: 1,
                }}
              />
            )}
          </Paper>
          {errors.mainImage && (
            <Typography color='error' variant='caption'>
              {errors.mainImage}
            </Typography>
          )}
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Typography variant='h6' gutterBottom>
            {t('Gallery Images')}
          </Typography>
          <Paper
            {...getGalleryRootProps()}
            sx={{
              p: 2,
              border: '2px dashed',
              borderColor: 'primary.main',
              textAlign: 'center',
              cursor: 'pointer',
              bgcolor: 'background.default',
            }}
          >
            <input {...getGalleryInputProps()} />
            <CloudUploadIcon
              sx={{ fontSize: 40, color: 'primary.main', mb: 1 }}
            />
            <Typography>
              {t('Drop gallery images here or click to select')}
            </Typography>
          </Paper>
          {(existingGalleryImages?.length > 0 ||
            newGalleryImages?.length > 0) && (
            <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {existingGalleryImages?.map?.((imgPath, index) => (
                <Box
                  key={`existing-${index}`}
                  sx={{ position: 'relative', width: 100, height: 100 }}
                >
                  <GalleryImageBlob
                    imgPath={imgPath}
                    apiUrl={apiUrl}
                    alt={`Gallery ${index + 1}`}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: 1,
                    }}
                  />
                  <IconButton
                    size='small'
                    sx={{
                      position: 'absolute',
                      top: -8,
                      right: -8,
                      bgcolor: 'background.paper',
                    }}
                    onClick={() => handleRemoveExistingGalleryImage(index)}
                  >
                    <DeleteIcon fontSize='small' />
                  </IconButton>
                </Box>
              ))}
              {newGalleryImages?.map?.((image, index) => (
                <Box
                  key={`new-${index}`}
                  sx={{ position: 'relative', width: 100, height: 100 }}
                >
                  <Box
                    component='img'
                    src={URL.createObjectURL(image)}
                    alt={`Gallery new ${index + 1}`}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: 1,
                    }}
                  />
                  <IconButton
                    size='small'
                    sx={{
                      position: 'absolute',
                      top: -8,
                      right: -8,
                      bgcolor: 'background.paper',
                    }}
                    onClick={() => handleRemoveNewGalleryImage(index)}
                  >
                    <DeleteIcon fontSize='small' />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
            mb={2}
          >
            <Typography variant='h6'>{t('Specifications')}</Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={addSpecification}
              variant='outlined'
              size='small'
            >
              {t('Add Specification')}
            </Button>
          </Box>

          {formData.specifications.map((spec, index) => (
            <Box key={index} sx={{ mb: 2, display: 'flex', gap: 2 }}>
              <TextField
                label={t('Specification Name')}
                value={spec.name}
                onChange={(e) =>
                  handleSpecificationChange(index, 'name', e.target.value)
                }
                error={!!errors.specifications?.[index]?.name}
                helperText={errors.specifications?.[index]?.name}
                sx={{ flex: 1 }}
              />
              <TextField
                label={t('Specification Value')}
                value={spec.value}
                onChange={(e) =>
                  handleSpecificationChange(index, 'value', e.target.value)
                }
                error={!!errors.specifications?.[index]?.value}
                helperText={errors.specifications?.[index]?.value}
                sx={{ flex: 1 }}
              />
              <IconButton
                color='error'
                onClick={() => removeSpecification(index)}
                disabled={formData.specifications.length === 1}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Box display='flex' justifyContent='flex-end' gap={2}>
            <Button onClick={onCancel} variant='outlined'>
              {t('Cancel')}
            </Button>
            <Button type='submit' variant='contained'>
              {product ? t('Update') : t('Create')}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProductForm;
